import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import type { FieldProps, Server } from "@/app/servers/types";

export const SecretField = ({
  form,
  canUpdate,
  isNew,
  server,
}: FieldProps & { isNew: boolean; server?: Server }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isEditing = !!server;
  const isManaged = server?.managed ?? false;
  const secretMeta = server?.type === "http" ? server.http?.secret : undefined;
  const secretExists = secretMeta?.exists ?? false;

  // For managed servers, just show that secret exists without the input
  if (isManaged && secretExists) {
    return (
      <FormItem>
        <FormLabel>Secret</FormLabel>
        <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="font-medium text-xs">A secret is set</span>
          </div>
        </div>
        <FormDescription>
          This secret is managed by Arcade and cannot be changed.
        </FormDescription>
      </FormItem>
    );
  }

  return (
    <FormField
      control={form.control}
      name="http.secret"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Secret</FormLabel>
          {!!(isEditing && secretExists) && (
            <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                <span className="font-medium text-xs">A secret is set</span>
              </div>
            </div>
          )}
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                autoComplete="secret"
                autoFocus={isNew}
                disabled={!canUpdate}
                placeholder="dev"
                type={showPassword ? "text" : "password"}
              />
              <Button
                className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                disabled={!canUpdate}
                onClick={() => setShowPassword(!showPassword)}
                size="sm"
                type="button"
                variant="ghost"
              >
                {showPassword ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
          </FormControl>
          <FormDescription>
            The secret used to authenticate with the server. Make sure to set
            the <code>ARCADE_WORKER_SECRET</code> environment variable on your
            server before entering it here.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
